import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import BrandLogo from "../../components/common/BrandLogo";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store";
import { verifyEmail, resendVerification, clearError } from "../../features/auth/authSlice";
import "../../styles/auth.scss";
import Swal from "sweetalert2";

interface VerificationFormInputs {
    code: string;
}

const schema = yup.object().shape({
    code: yup
        .string()
        .required("Verification code is required")
        .matches(/^\d{6}$/, "Code must be exactly 6 digits"),
});

const VerificationPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);

    // Get email from location state or localStorage (saved during registration)
    // We need to implement a way to pass email. For now, let's assume it's passed via state
    // or we can store it in a temporary slice/localStorage.
    // Let's assume the user just registered and we have their email in the form state or similar.
    // Actually, better to store "pending_verification_email" in localStorage during register.
    const [email, setEmail] = useState<string>("");
    const [resendDisabled, setResendDisabled] = useState(false);
    const [countdown, setCountdown] = useState(180);

    useEffect(() => {
        // Check for email in location state first, then localStorage
        const stateEmail = location.state?.email;
        const storedEmail = localStorage.getItem("pending_verification_email");

        if (stateEmail) {
            setEmail(stateEmail);
        } else if (storedEmail) {
            setEmail(storedEmail);
        } else {
            // If no email found, redirect to login
            navigate("/login");
        }

        return () => {
            dispatch(clearError());
        };
    }, [location, navigate, dispatch]);

    const { user } = useAppSelector((state) => state.auth);

    useEffect(() => {
        if (isAuthenticated && user) {
            // Wait for full user data (id should not be -1)
            if (user.id === -1) return;

            // Check roles
            const isAgencyOwner = user.roles.includes("agency_owner");
            const isCompanyOwner = user.roles.includes("company_owner");
            const isAdmin = user.roles.includes("admin") || user.roles.includes("superadmin");
            const isSpecialist = user.roles.includes("marketing_specialist") || user.roles.includes("company_specialist");

            if (isAdmin || isSpecialist) {
                navigate("/dashboard");
            } else if (isAgencyOwner || isCompanyOwner) {
                if (user.companies && user.companies.length > 0) {
                    navigate("/dashboard");
                } else {
                    navigate("/onboarding");
                }
            } else {
                // Fallback
                navigate("/dashboard");
            }
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            setResendDisabled(true);
        } else {
            setResendDisabled(false);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<VerificationFormInputs>({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data: VerificationFormInputs) => {
        if (!email) return;

        const resultAction = await dispatch(verifyEmail({ email, code: data.code }));
        if (verifyEmail.fulfilled.match(resultAction)) {
            // Success will be handled by isAuthenticated effect
            localStorage.removeItem("pending_verification_email");
            Swal.fire({
                icon: "success",
                title: "Email Verified Successfully!",
                html: `<p style="margin: 0; font-size: 15px; color: #64748b;">Welcome aboard! Taking you to your dashboard...</p>`,
                timer: 2000,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-modern-popup',
                    title: 'swal-modern-title',
                    htmlContainer: 'swal-modern-html'
                }
            }).then(() => {
                // Redirect will be handled by the useEffect
            });
        }
    };

    const handleResend = async () => {
        if (!email) return;

        setResendDisabled(true);
        setCountdown(180); // 180 seconds cooldown

        const resultAction = await dispatch(resendVerification(email));
        if (resendVerification.fulfilled.match(resultAction)) {
            Swal.fire({
                icon: "success",
                title: "New Code Sent!",
                html: `<p style="margin: 0; font-size: 15px; color: #64748b;">We've sent a fresh verification code to</p><p style="margin: 8px 0 0 0; font-size: 15px; color: #1e293b; font-weight: 600;">${email}</p>`,
                timer: 3000,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-modern-popup',
                    title: 'swal-modern-title',
                    htmlContainer: 'swal-modern-html'
                }
            });
        }
    };

    return (
        <div className="auth-page verification-page">
            <div className="gradient-orb"></div>
            <div className="auth-container">
                <div className="auth-card-wrapper">
                    <div className="auth-card">
                        <div className="auth-logo">
                            <BrandLogo height={28} />
                        </div>


                        <h2 className="form-title">Verify Your Email</h2>
                        <p className="form-subtitle">
                            We've sent a 6-digit code to <br />
                            <strong>{email}</strong>
                        </p>

                        {error && (
                            <div className="alert" role="alert">
                                {error}
                            </div>
                        )}

                        <form className="auth-form verification-form" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-group">
                                <label className="code-input-label">Enter Verification Code</label>
                                <div className="code-input-wrapper">
                                    <input
                                        type="text"
                                        className={`verification-code-input ${errors.code ? "is-invalid" : ""}`}
                                        id="code"
                                        placeholder="000000"
                                        maxLength={6}
                                        {...register("code")}
                                        autoComplete="off"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                    />
                                    {errors.code && (
                                        <div className="error-message">{errors.code.message}</div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`verify-button ${isLoading ? "loading" : ""}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner" role="status" aria-hidden="true"></span>
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Verify Email
                                    </>
                                )}
                            </button>

                            <div className="resend-section">
                                <button
                                    type="button"
                                    className={`resend-button ${resendDisabled ? "disabled" : ""}`}
                                    onClick={handleResend}
                                    disabled={resendDisabled || isLoading}
                                >
                                    {resendDisabled ? (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <polyline points="12 6 12 12 16 14" />
                                            </svg>
                                            Resend in {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                                        </>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                                <path d="M21 3v5h-5" />
                                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                                <path d="M3 21v-5h5" />
                                            </svg>
                                            Resend Code
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="back-link-wrapper">
                                <button
                                    type="button"
                                    className="back-link"
                                    onClick={() => navigate("/login")}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 12H5M12 19l-7-7 7-7" />
                                    </svg>
                                    Back to Login
                                </button>
                            </div>
                        </form>
                    </div>


                    <div className="copyright">
                        <div className="copyright-content">
                            <span>&copy; {new Date().getFullYear()} Meylo</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerificationPage;
